import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { GitPullRequest, Plus, CheckCircle, XCircle, Clock, Loader2, ChevronDown, ChevronUp, ArrowUpDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColors = { open: "#FFB800", accepted: "#00F0FF", rejected: "#FF3B30" };
const statusLabels = { open: "Offen", accepted: "Angenommen", rejected: "Abgelehnt" };

export default function ChangeRequestPanel({ projectId }) {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", impact_hours: "" });
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchCRs = () => {
    axios.get(`${API}/projects/${projectId}/change-requests`, { withCredentials: true })
      .then(res => setRequests(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCRs(); }, [projectId]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/change-requests`, {
        title: form.title, description: form.description,
        impact_hours: form.impact_hours ? parseInt(form.impact_hours) : null,
      }, { withCredentials: true });
      setForm({ title: "", description: "", impact_hours: "" });
      setShowForm(false);
      fetchCRs();
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const handleRespond = async (crId, status) => {
    try {
      await axios.post(`${API}/projects/${projectId}/change-requests/${crId}/respond`,
        { status }, { withCredentials: true });
      fetchCRs();
    } catch (err) { console.error(err); }
  };

  const openCount = requests.filter(r => r.status === "open").length;

  return (
    <div data-testid="change-request-panel" className="glass-surface rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-[#FFB800]" />
          <h3 className="font-['Outfit'] text-sm font-semibold text-white">Aenderungsvorschlaege</h3>
          {openCount > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FFB800] text-black text-[10px] font-bold">{openCount}</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#606070]" /> : <ChevronDown className="w-4 h-4 text-[#606070]" />}
      </button>

      {expanded && (
        <div className="border-t border-white/5">
          {/* New CR button */}
          <div className="px-5 py-3 border-b border-white/5">
            {!showForm ? (
              <button data-testid="new-change-request-btn" onClick={() => setShowForm(true)}
                className="btn-secondary w-full flex items-center justify-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> Aenderung vorschlagen
              </button>
            ) : (
              <div className="space-y-3">
                <input data-testid="cr-title" type="text" value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Titel der Aenderung"
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] px-3 py-2 text-sm outline-none rounded-lg" />
                <textarea data-testid="cr-description" rows={2} value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Was soll geaendert werden und warum?"
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] px-3 py-2 text-sm outline-none resize-none rounded-lg" />
                <input data-testid="cr-hours" type="number" value={form.impact_hours}
                  onChange={e => setForm(p => ({ ...p, impact_hours: e.target.value }))}
                  placeholder="Geschaetzter Mehraufwand in Stunden (optional)"
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] px-3 py-2 text-sm outline-none rounded-lg font-['JetBrains_Mono']" />
                <div className="flex gap-2">
                  <button data-testid="cr-submit" onClick={handleCreate} disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#FFB800]/10 border border-[#FFB800]/20 text-[#FFB800] text-sm rounded-lg hover:bg-[#FFB800]/20 transition-colors font-['Outfit'] font-medium">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitPullRequest className="w-4 h-4" />}
                    Vorschlag senden
                  </button>
                  <button onClick={() => setShowForm(false)} className="btn-secondary text-sm px-4">Abbrechen</button>
                </div>
              </div>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 text-[#FFB800] animate-spin" /></div>
          ) : requests.length === 0 ? (
            <div className="px-5 py-6 text-center">
              <ArrowUpDown className="w-8 h-8 text-[#606070] mx-auto mb-2" />
              <p className="text-xs text-[#606070]">Keine Aenderungsvorschlaege</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {requests.map(cr => (
                <div key={cr.id} className="px-5 py-3">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <span className="text-sm text-white font-medium">{cr.title}</span>
                      <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] ml-2">
                        von {cr.author_name} ({cr.author_role === "client" ? "Auftraggeber" : "Auftragnehmer"})
                      </span>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-['JetBrains_Mono']"
                      style={{ color: statusColors[cr.status], background: `${statusColors[cr.status]}15` }}>
                      {statusLabels[cr.status]}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#808090] mb-2">{cr.description}</p>
                  {cr.impact_hours && (
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[#FFB800]">
                      <Clock className="w-3 h-3 inline mr-1" />+{cr.impact_hours}h Mehraufwand
                    </span>
                  )}
                  {cr.status === "open" && cr.author_id !== user?.user_id && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => handleRespond(cr.id, "accepted")}
                        className="flex items-center gap-1 px-2 py-1 bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF] text-[10px] rounded-lg">
                        <CheckCircle className="w-3 h-3" /> Annehmen
                      </button>
                      <button onClick={() => handleRespond(cr.id, "rejected")}
                        className="flex items-center gap-1 px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] text-[10px] rounded-lg">
                        <XCircle className="w-3 h-3" /> Ablehnen
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
